'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const layers = [
  {
    num: '01',
    title: 'Business Planning Architecture',
    desc: 'Define planning strategy, corporate model, governance, and planning calendars.',
    span: 'col-span-2',
  },
  {
    num: '02',
    title: 'Planning Data Architecture',
    desc: 'Planning models, dimensions, hierarchies, version management, currency handling.',
    span: 'col-span-1',
  },
  {
    num: '03',
    title: 'Data Integration Layer',
    desc: 'S/4HANA integration, SAP Datasphere, flat file and API integrations, data quality.',
    span: 'col-span-1',
  },
  {
    num: '04',
    title: 'Planning Model Design',
    desc: 'Fact models, dimension models, star schema design, measure design, allocation logic.',
    span: 'col-span-2',
  },
  {
    num: '05',
    title: 'Planning Logic Layer',
    desc: 'Data actions, advanced formulas, allocation engines, driver-based planning, scenario modelling.',
    span: 'col-span-1',
  },
  {
    num: '06',
    title: 'Planning Experience Layer',
    desc: 'Planning stories, input forms, variance dashboards, workflow tasks, approval processes.',
    span: 'col-span-1',
  },
  {
    num: '07',
    title: 'Planning Governance & Operations',
    desc: 'Planning cycles, forecast cadence, access controls, auditability, performance optimization.',
    span: 'col-span-2',
  },
];

export default function FrameworkPreview() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 border-b border-border bg-white">
      <div className="max-w-container mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-12 reveal-on-scroll">
          <div>
            <div className="mono-label mb-3">Core Intellectual Property</div>
            <h2
              className="font-heading font-semibold text-foreground"
              style={{ fontSize: 'clamp(1.75rem, 3vw, 2.75rem)', letterSpacing: '-0.025em' }}
            >
              The 7-Layer SAC Planning Architecture
            </h2>
          </div>
          <Link
            href="/sac-planning-framework"
            className="hidden md:flex items-center gap-2 text-sm font-medium text-muted hover:text-foreground transition-colors duration-200 group"
          >
            View Full Framework
            <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
          </Link>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {layers.map((layer, i) => (
            <div
              key={layer.num}
              className={`layer-card reveal-on-scroll reveal-delay-${Math.min(i + 1, 6)} border border-border p-6 cursor-default ${
                layer.span === 'col-span-2' ? 'md:col-span-2' : 'md:col-span-1'
              }`}
              style={{ borderRadius: '4px' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="layer-number mono-label mb-3 block">{layer.num}</span>
                  <h3 className="font-heading font-semibold text-foreground text-base mb-2">
                    {layer.title}
                  </h3>
                  <p className="layer-desc text-sm text-muted leading-relaxed font-body">
                    {layer.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex md:hidden">
          <Link href="/sac-planning-framework" className="btn-outline text-sm">
            View Full Framework →
          </Link>
        </div>
      </div>
    </section>
  );
}